<template>
    <div class="min-h-screen bg-background flex items-center justify-center p-6 font-sans">
        <div class="w-full max-w-4xl space-y-8 animate-slide-down">

            <!-- Header -->
            <header class="text-center space-y-2">
                <h1 class="text-3xl font-bold text-gold tracking-wide">
                    💸 Montez au classement
                </h1>
                <p class="text-secondary">
                    1€ versé = <span class="text-gold font-semibold">1 point</span>.<br />
                    Simulez votre nouvelle position avant de payer.
                </p>
            </header>

            <section v-if="paymentFeedback.message" :class="feedbackClass">
                {{ paymentFeedback.message }}
            </section>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

                <!-- LEFT: FORMULAIRE DE PAIEMENT -->
                <div class="lg:col-span-1 space-y-6">

                    <!-- User info -->
                    <section class="card-primary">
                        <p class="text-sm text-secondary">Connecté en tant que</p>
                        <p class="text-lg font-semibold text-primary">{{ user.username || '—' }}</p>
                        <p class="text-sm text-secondary mt-2">Montant actuel : <span class="font-bold text-gold">{{
                            user.amount || 0 }}€ ({{ user.amount || 0 }} pts)</span></p>
                    </section>

                    <!-- Montant à verser -->
                    <section class="space-y-3">
                        <h2 class="text-lg font-semibold text-gold">Montant à verser</h2>
                        <div>
                            <label class="text-sm text-secondary">Montant en euros</label>
                            <input type="number" min="1" v-model.number="amount" placeholder="Entre un montant"
                                class="w-full bg-backgroundLight border border-gold rounded-lg px-4 py-3 text-primary focus:outline-none focus:ring-2 focus:ring-gold" />
                        </div>

                        <div class="space-y-2">
                            <p class="text-xs text-secondary">Montants rapides</p>
                            <div class="flex flex-wrap gap-2">
                                <button v-for="preset in suggestedAmounts" :key="preset" type="button"
                                    class="rounded-full border border-gold/30 px-3 py-1 text-sm text-gold hover:bg-gold hover:text-primary transition"
                                    @click="amount = preset">
                                    {{ preset }}€
                                </button>
                            </div>
                        </div>
                    </section>

                    <!-- Bouton paiement -->
                    <button class="btn-gold w-full text-lg py-3" :disabled="!canPay || loading" @click="startPayment">
                        <span v-if="!loading">Payer {{ amount || 0 }}€ ({{ amount || 0 }} pts)</span>
                        <span v-else class="flex items-center justify-center gap-2">
                            <span class="loader-sm"></span>
                            Paiement en cours…
                        </span>
                    </button>

                    <div v-if="amount >= 1" class="text-sm text-center text-secondary space-y-1">
                        <p>
                            Après ce versement, votre rang estimé serait
                            <span class="font-semibold text-gold">#{{ simulatedRanks.general?.position || '—' }}</span>
                            au classement général.
                        </p>
                        <p class="text-xs text-secondary">
                            Pour passer devant un utilisateur déjà à ce niveau, il faut strictement dépasser son
                            montant.
                        </p>
                        <p v-if="progressMessage" class="text-gold font-semibold">
                            {{ progressMessage }}
                        </p>
                        <p v-if="milestoneMessage" class="text-xs text-secondary">
                            {{ milestoneMessage }}
                        </p>
                    </div>

                    <!-- Disclaimer -->
                    <div class="text-xs text-secondary space-y-1">
                        <p>🔒 Paiement sécurisé via Stripe</p>
                        <p>✓ Crédité instantanément</p>
                        <p class="italic text-[10px]">
                            Montants définitifs, non remboursables. Pour établir un classement public uniquement.
                        </p>
                    </div>

                </div>

                <!-- RIGHT: RÉCAPITULATIF & CLASSEMENTS SIMULÉS -->
                <div class="lg:col-span-2 space-y-6">

                    <!-- Récap du nouvel état -->
                    <section class="card-gold-premium space-y-4">
                        <h2 class="text-lg font-semibold text-gold">Récapitulatif</h2>

                        <div class="grid grid-cols-2 gap-4 text-sm">
                            <div class="bg-background p-4 rounded-lg border border-gold border-opacity-20">
                                <p class="text-gold font-semibold text-xs uppercase tracking-wide mb-2">Montant actuel
                                </p>
                                <p class="text-2xl font-bold text-primary">{{ user.amount || 0 }}€ <span
                                        class="text-lg text-secondary">({{ user.amount || 0 }} pts)</span></p>
                            </div>
                            <div class="bg-background p-4 rounded-lg border border-gold border-opacity-20">
                                <p class="text-gold font-semibold text-xs uppercase tracking-wide mb-2">À ajouter</p>
                                <p class="text-2xl font-bold text-gold">+ {{ amount || 0 }}€ <span
                                        class="text-lg text-secondary">(+ {{ amount || 0 }} pts)</span></p>
                            </div>
                            <div class="bg-background p-4 rounded-lg border border-gold border-opacity-40 col-span-2">
                                <p class="text-gold font-semibold text-xs uppercase tracking-wide mb-2">Montant total
                                    après achat</p>
                                <p class="text-3xl font-bold text-gold">{{ (user.amount || 0) + (amount || 0) }}€ <span
                                        class="text-xl text-secondary">({{ (user.amount || 0) + (amount || 0) }}
                                        pts)</span></p>
                            </div>
                        </div>
                    </section>

                    <!-- Classements simulés -->
                    <section class="card-primary space-y-4">
                        <h2 class="text-lg font-semibold">Votre position estimée après paiement</h2>

                        <div v-if="amount && amount > 0" class="space-y-3">
                            <p class="text-sm text-secondary">Position estimée dans chaque classement :</p>

                            <!-- Grid 2x2 ou full width selon l'écran -->
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">

                                <!-- Général -->
                                <div class="bg-backgroundLight border border-gold border-opacity-30 p-4 rounded-lg">
                                    <p class="text-xs uppercase text-secondary font-semibold mb-2">Classement général
                                    </p>
                                    <div v-if="isSimulating" class="flex items-center gap-2">
                                        <span class="loader-sm"></span>
                                        <span class="text-sm text-secondary">Calcul…</span>
                                    </div>
                                    <div v-else-if="simulatedRanks.general" class="space-y-1">
                                        <p class="text-3xl font-bold text-gold">#{{ simulatedRanks.general.position }}
                                        </p>
                                        <p class="text-xs text-secondary">sur {{ simulatedRanks.general.total }}
                                            utilisateurs</p>
                                    </div>
                                    <div v-else class="text-secondary text-sm">—</div>
                                </div>

                                <!-- Régional -->
                                <div v-if="ranks.regional"
                                    class="bg-backgroundLight border border-gold border-opacity-30 p-4 rounded-lg">
                                    <p class="text-xs uppercase text-secondary font-semibold mb-2">Classement régional
                                    </p>
                                    <div v-if="isSimulating" class="flex items-center gap-2">
                                        <span class="loader-sm"></span>
                                        <span class="text-sm text-secondary">Calcul…</span>
                                    </div>
                                    <div v-else-if="simulatedRanks.regional" class="space-y-1">
                                        <p class="text-3xl font-bold text-gold">#{{ simulatedRanks.regional.position }}
                                        </p>
                                        <p class="text-xs text-secondary">{{ ranks.regional.regionName || '—' }} ({{
                                            simulatedRanks.regional.total }} utilisateurs)</p>
                                    </div>
                                    <div v-else class="text-secondary text-sm">—</div>
                                </div>
                                <div v-else class="bg-backgroundLight p-4 rounded-lg">
                                    <p class="text-xs text-secondary">Complétez votre région pour voir votre classement
                                        régional.</p>
                                </div>

                                <!-- Départemental -->
                                <div v-if="ranks.departmental"
                                    class="bg-backgroundLight border border-gold border-opacity-30 p-4 rounded-lg">
                                    <p class="text-xs uppercase text-secondary font-semibold mb-2">Classement
                                        départemental</p>
                                    <div v-if="isSimulating" class="flex items-center gap-2">
                                        <span class="loader-sm"></span>
                                        <span class="text-sm text-secondary">Calcul…</span>
                                    </div>
                                    <div v-else-if="simulatedRanks.departmental" class="space-y-1">
                                        <p class="text-3xl font-bold text-gold">#{{ simulatedRanks.departmental.position
                                            }}</p>
                                        <p class="text-xs text-secondary">{{ ranks.departmental.departmentName || '—' }}
                                            ({{ simulatedRanks.departmental.total }} utilisateurs)</p>
                                    </div>
                                    <div v-else class="text-secondary text-sm">—</div>
                                </div>
                                <div v-else class="bg-backgroundLight p-4 rounded-lg">
                                    <p class="text-xs text-secondary">Complétez votre département pour voir votre
                                        classement départemental.</p>
                                </div>

                                <!-- Communal -->
                                <div v-if="ranks.city"
                                    class="bg-backgroundLight border border-gold border-opacity-30 p-4 rounded-lg">
                                    <p class="text-xs uppercase text-secondary font-semibold mb-2">Classement communal
                                    </p>
                                    <div v-if="isSimulating" class="flex items-center gap-2">
                                        <span class="loader-sm"></span>
                                        <span class="text-sm text-secondary">Calcul…</span>
                                    </div>
                                    <div v-else-if="simulatedRanks.city" class="space-y-1">
                                        <p class="text-3xl font-bold text-gold">#{{ simulatedRanks.city.position }}</p>
                                        <p class="text-xs text-secondary">{{ ranks.city.cityName || '—' }} ({{
                                            simulatedRanks.city.total }} utilisateurs)</p>
                                    </div>
                                    <div v-else class="text-secondary text-sm">—</div>
                                </div>
                                <div v-else class="bg-backgroundLight p-4 rounded-lg">
                                    <p class="text-xs text-secondary">Complétez votre commune pour voir votre classement
                                        communal.</p>
                                </div>

                            </div>

                            <p class="text-xs text-secondary italic mt-4">
                                Les classements sont calculés sur votre futur montant après paiement. Pour dépasser un
                                profil, il faut strictement faire mieux que son montant ; en cas d’égalité, la priorité
                                reste à celui qui a atteint ce niveau en premier.
                            </p>
                        </div>

                        <div v-else class="text-center py-8 text-secondary">
                            <p>Entrez un montant pour voir votre position estimée</p>
                        </div>

                    </section>

                </div>

            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import RankingService from '@/services/ranking.service'
import PaymentService from '@/services/payment.service'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()
const emptyUser = { username: '—', amount: 0 }

/* STATE */
const user = ref(emptyUser)
const amount = ref(null)
const loading = ref(false)
const paymentFeedback = ref({ type: 'info', message: '' })
const suggestedAmounts = [5, 10, 20, 50]

// Classements actuels (pour afficher noms région/dept/city)
const ranks = ref({
    general: null,
    regional: null,
    departmental: null,
    city: null
})

// Classements simulés avec le nouvel montant
const simulatedRanks = ref({
    general: null,
    regional: null,
    departmental: null,
    city: null
})

const isSimulating = ref(false)

/* COMPUTED */
const canPay = computed(() => amount.value >= 1 && Boolean(authStore.user?.id))

const progressMessage = computed(() => {
    const currentPosition = ranks.value.general?.position
    const projectedPosition = simulatedRanks.value.general?.position

    if (!currentPosition || !projectedPosition || projectedPosition >= currentPosition) {
        return ''
    }

    const placesWon = currentPosition - projectedPosition

    if (placesWon === 1) {
        return 'Ce versement peut vous faire gagner 1 place.'
    }

    return `Ce versement peut vous faire gagner ${placesWon} places.`
})

const milestoneMessage = computed(() => {
    const currentPosition = ranks.value.general?.position
    const projectedPosition = simulatedRanks.value.general?.position

    if (!currentPosition || !projectedPosition) return ''

    if (currentPosition > 3 && projectedPosition <= 3) {
        return 'Cap possible : atteindre le podium.'
    }

    if (currentPosition > 10 && projectedPosition <= 10) {
        return 'Cap possible : entrer dans le top 10.'
    }

    if (currentPosition > 20 && projectedPosition <= 20) {
        return 'Cap possible : entrer dans le top 20.'
    }

    if (currentPosition <= 10 && projectedPosition < currentPosition) {
        return 'Cap possible : consolider votre place parmi les meilleurs.'
    }

    return ''
})

const feedbackClass = computed(() => {
    const baseClass = 'rounded-lg border px-4 py-3 text-sm font-semibold shadow-sm '

    if (paymentFeedback.value.type === 'success') {
        return `${baseClass}border-emerald-400 bg-emerald-950/40 text-emerald-50`
    }

    if (paymentFeedback.value.type === 'error') {
        return `${baseClass}border-red-400 bg-red-950/40 text-red-50`
    }

    return `${baseClass}border-gold border-opacity-30 bg-backgroundLight text-primary`
})

function setPaymentFeedback(type, message) {
    paymentFeedback.value = { type, message }
}

/* LOADERS */
async function loadUserProfile() {
    if (!authStore.user?.id) {
        try {
            await authStore.fetchUser()
        } catch (e) {
            console.error('Erreur chargement utilisateur:', e)
        }
    }

    user.value = authStore.user || emptyUser
}

async function loadRanks() {
    const userId = authStore.user?.id
    if (!userId) return

    try {
        const all = await RankingService.getAllRanks(userId)
        ranks.value = {
            general: all.general,
            regional: all.regional,
            departmental: all.departmental,
            city: all.city
        }
    } catch (e) {
        console.error('Erreur chargement ranks:', e)
    }
}

/* SIMULATEUR */
async function simulateRanks() {
    const amountValue = Number(amount.value || 0)

    if (amountValue <= 0) {
        simulatedRanks.value = {
            general: null,
            regional: null,
            departmental: null,
            city: null
        }
        return
    }

    isSimulating.value = true

    try {
        const calls = []
        const userId = authStore.user?.id
        const currentAmount = Number(authStore.user?.amount || 0)
        const totalAmount = currentAmount + amountValue

        calls.push(
            RankingService.getSimulatedRank({ userId, amount: totalAmount })
        )

        if (ranks.value.regional) {
            calls.push(
                RankingService.getSimulatedRank({
                    userId,
                    amount: totalAmount,
                    regionId: authStore.user.region_id
                })
            )
        } else {
            calls.push(Promise.resolve(null))
        }

        if (ranks.value.departmental) {
            calls.push(
                RankingService.getSimulatedRank({
                    userId,
                    amount: totalAmount,
                    departmentId: authStore.user.department_id
                })
            )
        } else {
            calls.push(Promise.resolve(null))
        }

        if (ranks.value.city) {
            calls.push(
                RankingService.getSimulatedRank({
                    userId,
                    amount: totalAmount,
                    cityId: authStore.user.city_id
                })
            )
        } else {
            calls.push(Promise.resolve(null))
        }

        const [general, regional, departmental, city] = await Promise.all(calls)

        simulatedRanks.value = { general, regional, departmental, city }
    } catch (e) {
        console.error('Erreur simulation classement:', e)
    } finally {
        isSimulating.value = false
    }
}

async function handlePaymentReturn() {
    const paymentStatus = route.query.payment
    const sessionId = route.query.session_id

    if (!paymentStatus) return

    if (paymentStatus === 'cancelled') {
        setPaymentFeedback('info', 'Le paiement a été annulé. Tu peux modifier le montant et réessayer.')
        await router.replace({ path: route.path, query: {} })
        return
    }

    if (paymentStatus === 'success') {
        try {
            if (sessionId) {
                await PaymentService.confirmCheckoutSession(sessionId)
            }

            await authStore.fetchUser()
            user.value = authStore.user || emptyUser
            await loadRanks()
            await simulateRanks()

            setPaymentFeedback('success', 'Paiement confirmé. Ton montant a bien été crédité.')
        } catch (e) {
            console.error('Erreur confirmation paiement:', e)
            setPaymentFeedback('error', 'Le paiement semble validé, mais la mise à jour est encore en cours. Recharge la page dans quelques secondes.')
        } finally {
            await router.replace({ path: route.path, query: {} })
        }
    }
}

let simTimeout = null
watch(
    () => amount.value,
    () => {
        clearTimeout(simTimeout)
        simTimeout = setTimeout(simulateRanks, 300)
    }
)

/* PAIEMENT */
const startPayment = async () => {
    if (!canPay.value) return

    loading.value = true
    setPaymentFeedback('info', '')

    try {
        const response = await PaymentService.createCheckoutSession(amount.value)

        if (!response?.url) {
            throw new Error('Stripe checkout URL is missing.')
        }

        window.location.href = response.url
    } catch (e) {
        console.error('Erreur lancement paiement:', e)
        const backendMessage = e?.response?.data?.message
        setPaymentFeedback('error', backendMessage || 'Impossible de démarrer le paiement. Vérifie la configuration Stripe du serveur.')
        loading.value = false
    }
}

/* INIT */
onMounted(async () => {
    await loadUserProfile()
    await loadRanks()

    const presetAmount = Number(route.query.amount || 0)
    if (presetAmount >= 1) {
        amount.value = presetAmount
    }

    await handlePaymentReturn()
})
</script>
